import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import GppMaybeOutlinedIcon from "@mui/icons-material/GppMaybeOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import SearchIcon from "@mui/icons-material/Search";
import TuneIcon from "@mui/icons-material/Tune";
import {
  CATEGORY_COLORS,
  CATEGORY_COLOR_FALLBACK,
  DOC_SCOPE_TOOL_NAMES,
  PAIRED_PARENT_TOOL_LABELS,
  TABLE_SCOPE_TOOL_NAMES,
  formatToolNameAsLabel,
} from "./agentConfig/toolCatalog";
import { TOOL_ICON_MAP } from "./agentConfig/toolPresentation";

function getCategoryColor(title, category) {
  if (category?.color) return category.color;
  return CATEGORY_COLORS[title] || CATEGORY_COLOR_FALLBACK;
}

function flattenTools(toolCategories) {
  const out = [];
  toolCategories.forEach((category) => {
    category.tools.forEach((tool) => {
      out.push({ ...tool, categoryTitle: category.title, category });
    });
  });
  return out;
}

const TOOL_TILE_SIZE_PX = 88;
const CUSTOM_TOOLS_CATEGORY = "Custom";
const NEW_CUSTOM_TOOL_PATH = "/custom_functions/new";

function buildCategoryTypeLabel(tool) {
  const parts = [tool.categoryTitle];
  if (
    TABLE_SCOPE_TOOL_NAMES.has(tool.name) ||
    DOC_SCOPE_TOOL_NAMES.has(tool.name)
  ) {
    parts.push("scoped");
  }
  if (tool.parentToolName) parts.push("linked");
  return parts.join(" · ");
}

function resolveFullCategory(toolCategories, displayCategory) {
  const categories = Array.isArray(toolCategories) ? toolCategories : [];
  const match = categories.find((candidate) => {
    if (displayCategory?.server?.serverId) {
      return candidate.server?.serverId === displayCategory.server.serverId;
    }
    return candidate.title === displayCategory.title;
  });
  return match || displayCategory;
}

export default function AgentToolLoadout({
  toolCategories,
  inventoryCategories,
  enabledTools,
  onToggleTool,
  onBulkSetToolsEnabled,
  toolInstructions = {},
  onUpdateToolInstruction,
  toolSearchQuery,
  setToolSearchQuery,
  loadingToolCatalog = false,
  includeCustomToolsCategory = true,
  renderToolConfig,
  renderCategoryConfig,
  approvalRequiredTools = [],
  onToggleToolApproval,
}) {
  const navigate = useNavigate();
  const effectiveInventoryCategories = inventoryCategories || toolCategories;
  const displayInventoryCategories = useMemo(() => {
    const categories = Array.isArray(effectiveInventoryCategories)
      ? effectiveInventoryCategories
      : [];
    const query = String(toolSearchQuery || "").trim();
    if (query) return categories;
    if (!includeCustomToolsCategory) return categories;
    if (categories.some((category) => category.title === CUSTOM_TOOLS_CATEGORY)) {
      return categories;
    }
    return [{ title: CUSTOM_TOOLS_CATEGORY, tools: [] }, ...categories];
  }, [effectiveInventoryCategories, toolSearchQuery, includeCustomToolsCategory]);
  const allTools = useMemo(
    () => flattenTools(toolCategories),
    [toolCategories]
  );
  const toolByName = useMemo(
    () => new Map(allTools.map((t) => [t.name, t])),
    [allTools]
  );
  const [detailToolName, setDetailToolName] = useState(null);

  const instructionsEnabledCount = useMemo(
    () =>
      Object.keys(toolInstructions).filter((name) =>
        enabledTools.includes(name)
      ).length,
    [toolInstructions, enabledTools]
  );

  const approvalEnabledCount = useMemo(
    () =>
      approvalRequiredTools.filter((name) => enabledTools.includes(name))
        .length,
    [approvalRequiredTools, enabledTools]
  );

  const detailTool = detailToolName ? toolByName.get(detailToolName) : null;

  const openDetail = (toolName) => setDetailToolName(toolName);
  const closeDetail = () => setDetailToolName(null);

  const handleTileToggle = (tool) => {
    if (tool.disableStandaloneToggle) return;
    onToggleTool(tool.name);
  };

  const renderCreateCustomToolTile = () => {
    const color = getCategoryColor(CUSTOM_TOOLS_CATEGORY);

    return (
      <Box
        role="link"
        tabIndex={0}
        aria-label="Create custom tool"
        onClick={() => navigate(NEW_CUSTOM_TOOL_PATH)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") {
            e.preventDefault();
            navigate(NEW_CUSTOM_TOOL_PATH);
          }
        }}
        sx={{
          width: TOOL_TILE_SIZE_PX,
          height: TOOL_TILE_SIZE_PX,
          maxWidth: "100%",
          boxSizing: "border-box",
          borderRadius: "8px",
          border: "1px dashed",
          borderColor: alpha(color, 0.45),
          backgroundColor: alpha(color, 0.04),
          padding: "8px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          transition:
            "transform 0.12s, box-shadow 0.12s, border-color 0.12s, background-color 0.12s",
          "&:hover": {
            transform: "translateY(-1px)",
            borderColor: color,
            backgroundColor: alpha(color, 0.1),
            boxShadow: (theme) =>
              `0 2px 8px ${alpha(theme.palette.common.black, 0.06)}`,
            zIndex: 1,
          },
        }}
      >
        <AddIcon sx={{ fontSize: 26, color }} />
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 600,
            color: "text.primary",
            textAlign: "center",
            mt: 0.5,
            lineHeight: 1.2,
            maxWidth: "100%",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
          }}
        >
          Create custom tool
        </Typography>
      </Box>
    );
  };

  const renderTile = (tool) => {
    const color = getCategoryColor(tool.categoryTitle, tool.category);
    const Icon = TOOL_ICON_MAP[tool.name] || tool.Icon || BuildOutlinedIcon;
    const isEquipped = enabledTools.includes(tool.name);
    const isScoped =
      TABLE_SCOPE_TOOL_NAMES.has(tool.name) ||
      DOC_SCOPE_TOOL_NAMES.has(tool.name);
    const isPaired = Boolean(tool.parentToolName);
    const isGated = isEquipped && approvalRequiredTools.includes(tool.name);
    const isActiveDetail = detailToolName === tool.name;

    return (
      <Box
        key={tool.name}
        role="button"
        tabIndex={0}
        aria-label={`Open details for ${tool.label}`}
        onClick={(e) => {
          if (e.shiftKey) {
            handleTileToggle(tool);
            return;
          }
          openDetail(tool.name);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") {
            e.preventDefault();
            if (e.shiftKey) {
              handleTileToggle(tool);
            } else {
              openDetail(tool.name);
            }
          }
        }}
        sx={{
          width: TOOL_TILE_SIZE_PX,
          height: TOOL_TILE_SIZE_PX,
          maxWidth: "100%",
          boxSizing: "border-box",
          borderRadius: "8px",
          border: "1px solid",
          borderColor: isActiveDetail
            ? color
            : isEquipped
            ? color
            : alpha(color, 0.25),
          backgroundColor: isEquipped
            ? alpha(color, 0.08)
            : "transparent",
          boxShadow: isEquipped ? `inset 2px 0 0 ${color}` : "none",
          padding: "8px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          cursor: "pointer",
          transition:
            "transform 0.12s, box-shadow 0.12s, border-color 0.12s, background-color 0.12s",
          "&:hover": {
            transform: "translateY(-1px)",
            borderColor: color,
            backgroundColor: isEquipped
              ? alpha(color, 0.12)
              : alpha(color, 0.05),
            boxShadow: isEquipped
              ? (theme) =>
                  `inset 2px 0 0 ${color}, 0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`
              : (theme) =>
                  `0 2px 8px ${alpha(theme.palette.common.black, 0.06)}`,
            zIndex: 1,
          },
        }}
      >
        {tool.tileGraphic ?? <Icon sx={{ fontSize: 26, color }} />}
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: isEquipped ? 600 : 500,
            color: "text.primary",
            textAlign: "center",
            mt: 0.5,
            lineHeight: 1.2,
            maxWidth: "100%",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
          }}
        >
          {tool.label}
        </Typography>
        {isScoped && (
          <TuneIcon
            sx={{
              position: "absolute",
              top: 4,
              right: 5,
              fontSize: 12,
              color: "warning.main",
            }}
          />
        )}
        {isGated && (
          <GppMaybeOutlinedIcon
            sx={{
              position: "absolute",
              top: 4,
              left: 5,
              fontSize: 12,
              color: "warning.main",
            }}
          />
        )}
        {isPaired && (
          <LinkOutlinedIcon
            sx={{
              position: "absolute",
              bottom: 4,
              left: 5,
              fontSize: 12,
              color: alpha(color, 0.7),
            }}
          />
        )}
      </Box>
    );
  };

  // ─── Detail panel (right rail; catalog stays visible) ───
  const renderDetailPanel = () => {
    if (!detailTool) return null;
    const color = getCategoryColor(detailTool.categoryTitle, detailTool.category);
    const Icon = TOOL_ICON_MAP[detailTool.name] || detailTool.Icon || BuildOutlinedIcon;
    const isEquipped = enabledTools.includes(detailTool.name);
    const hasInstruction = toolInstructions[detailTool.name] != null;

    return (
      <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            pb: 1.25,
            mb: 1.5,
            borderBottom: 1,
            borderColor: "divider",
            flexShrink: 0,
          }}
        >
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: 0.6,
              textTransform: "uppercase",
              color: "text.disabled",
            }}
          >
            Tool details
          </Typography>
          <IconButton
            size="small"
            onClick={closeDetail}
            aria-label="Close tool details"
            sx={{ color: "text.secondary", ml: "auto" }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        <Box sx={{ mb: 2, flexShrink: 0 }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25, mb: 1.5 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "8px",
                backgroundColor: alpha(color, 0.12),
                color,
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
              }}
            >
              {detailTool.tileGraphic ?? <Icon sx={{ fontSize: 24 }} />}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{ fontSize: 15, fontWeight: 600, color: "text.primary" }}
              >
                {detailTool.label}
              </Typography>
              <Box
                sx={{
                  fontSize: 11,
                  color: "text.disabled",
                  mt: 0.5,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "2px",
                    backgroundColor: color,
                  }}
                />
                <span>{buildCategoryTypeLabel(detailTool)}</span>
              </Box>
            </Box>
          </Box>
          <Box
            component="button"
            disabled={detailTool.disableStandaloneToggle === true}
            onClick={() => {
              if (detailTool.disableStandaloneToggle === true) return;
              handleTileToggle(detailTool);
            }}
            sx={{
              font: "inherit",
              fontSize: 13,
              fontWeight: 600,
              width: "100%",
              padding: "8px 16px",
              borderRadius: "6px",
              border: "1px solid",
              borderColor: isEquipped ? alpha(color, 0.4) : color,
              backgroundColor: isEquipped ? "background.paper" : color,
              color: isEquipped ? color : "#fff",
              cursor: detailTool.disableStandaloneToggle ? "not-allowed" : "pointer",
              opacity: detailTool.disableStandaloneToggle ? 0.55 : 1,
              transition: "background-color 0.12s, border-color 0.12s",
              "&:hover": {
                backgroundColor: isEquipped ? alpha(color, 0.08) : color,
                borderColor: color,
                filter: isEquipped ? "none" : "brightness(0.9)",
              },
            }}
          >
            {detailTool.disableStandaloneToggle
              ? isEquipped
                ? "Equipped"
                : "Not equipped"
              : isEquipped
              ? "Unequip"
              : "Equip"}
          </Box>

          {isEquipped && onToggleToolApproval && (
            <Box sx={{ mt: 1.5 }}>
              <Box
                component="label"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  cursor: "pointer",
                  userSelect: "none",
                }}
              >
                <Checkbox
                  size="small"
                  checked={approvalRequiredTools.includes(detailTool.name)}
                  onChange={() => onToggleToolApproval(detailTool.name)}
                  sx={{
                    p: 0,
                    "& .MuiSvgIcon-root": { fontSize: 18 },
                    color: "text.disabled",
                    "&.Mui-checked": { color: "warning.main" },
                  }}
                />
                <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                  Require human approval before running
                </Typography>
              </Box>
              <Typography
                sx={{ fontSize: 11, color: "text.secondary", mt: 0.5, ml: 3 }}
              >
                Pauses the agent and asks you to approve each call to this tool
                before it runs.
              </Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", pr: 0.5 }}>
          <Box sx={{ mb: 2.5 }}>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: 0.6,
                textTransform: "uppercase",
                color: "text.disabled",
                mb: 0.75,
              }}
            >
              Description
            </Typography>
            <Typography sx={{ fontSize: 13, color: "text.primary", lineHeight: 1.5 }}>
              {detailTool.desc || "No description provided."}
            </Typography>
          </Box>

          {detailTool.parentToolName && (
            <Box sx={{ mb: 2.5 }}>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: 0.6,
                  textTransform: "uppercase",
                  color: "text.disabled",
                  mb: 0.75,
                }}
              >
                Dependency
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1.25,
                  py: 1,
                  backgroundColor: alpha(color, 0.08),
                  border: 1,
                  borderColor: alpha(color, 0.25),
                  borderRadius: "6px",
                  fontSize: 12,
                  color: "text.secondary",
                }}
              >
                <LinkOutlinedIcon sx={{ fontSize: 16, color }} />
                <span>
                  Automatically equipped with{" "}
                  {PAIRED_PARENT_TOOL_LABELS[detailTool.parentToolName] ||
                    formatToolNameAsLabel(detailTool.parentToolName)}
                  .
                </span>
              </Box>
            </Box>
          )}

          {isEquipped && onUpdateToolInstruction && (
            <Box sx={{ mb: 2.5 }}>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: 0.6,
                  textTransform: "uppercase",
                  color: "text.disabled",
                  mb: 0.75,
                }}
              >
                Custom Instructions
              </Typography>
              <Box
                component="label"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  mb: 0.75,
                  cursor: "pointer",
                  userSelect: "none",
                }}
              >
                <Checkbox
                  size="small"
                  checked={hasInstruction}
                  onChange={(e) => {
                    onUpdateToolInstruction(
                      detailTool.name,
                      e.target.checked ? "" : undefined
                    );
                  }}
                  sx={{
                    p: 0,
                    "& .MuiSvgIcon-root": { fontSize: 18 },
                    color: "text.disabled",
                    "&.Mui-checked": { color },
                  }}
                />
                <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                  Add custom instructions for this tool
                </Typography>
              </Box>
              {hasInstruction && (
                <TextField
                  className="org-styled-textfield"
                  size="small"
                  fullWidth
                  multiline
                  minRows={3}
                  maxRows={8}
                  placeholder={`e.g. "Always include a signature" or "Use workflow A for X"`}
                  value={toolInstructions[detailTool.name] || ""}
                  onChange={(e) =>
                    onUpdateToolInstruction(detailTool.name, e.target.value)
                  }
                />
              )}
            </Box>
          )}

          {(() => {
            const configNode = renderToolConfig?.(detailTool);
            if (!configNode) return null;
            return <Box sx={{ mb: 2.5 }}>{configNode}</Box>;
          })()}
        </Box>
      </Box>
    );
  };

  // ─── Inventory view ───
  const renderInventoryView = () => (
    <>
      <Box sx={{ pb: 1.5, borderBottom: 1, borderColor: "divider", mb: 1.5 }}>
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
            {enabledTools.length}
          </Box>{" "}
          equipped
          {instructionsEnabledCount > 0 && (
            <>
              {" · "}
              <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
                {instructionsEnabledCount}
              </Box>{" "}
              with custom instructions
            </>
          )}
          {approvalEnabledCount > 0 && (
            <>
              {" · "}
              <Box component="span" sx={{ fontWeight: 600, color: "warning.main" }}>
                {approvalEnabledCount}
              </Box>{" "}
              require approval
            </>
          )}
        </Typography>
        <TextField
          className="org-styled-textfield"
          fullWidth
          size="small"
          value={toolSearchQuery}
          onChange={(e) => setToolSearchQuery(e.target.value)}
          placeholder="Search tools..."
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: "text.disabled" }} />
              </InputAdornment>
            ),
          }}
          sx={{ mt: 1.25 }}
        />
      </Box>

      {loadingToolCatalog ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={20} sx={{ color: "text.secondary" }} />
        </Box>
      ) : displayInventoryCategories.length === 0 ? (
        <Box
          sx={{
            border: 1,
            borderColor: "divider",
            borderRadius: "8px",
            px: 2,
            py: 1.5,
            backgroundColor: "action.hover",
          }}
        >
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
            {toolSearchQuery
              ? `No tools match "${toolSearchQuery.trim()}".`
              : "No tools are available for this agent."}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          {displayInventoryCategories.map((category) => {
            const color = getCategoryColor(category.title, category);
            const total = category.tools.length;
            const eq = category.tools.filter((t) =>
              enabledTools.includes(t.name)
            ).length;
            const showCreateCustomToolTile =
              category.title === CUSTOM_TOOLS_CATEGORY &&
              !String(toolSearchQuery || "").trim();
            const catTools = category.tools.map((t) => ({
              ...t,
              categoryTitle: category.title,
              category,
            }));
            const catConfigNode = renderCategoryConfig?.(category);
            const fullCategory = resolveFullCategory(toolCategories, category);
            const togglableTools = category.tools.filter(
              (tool) => !tool.disableStandaloneToggle
            );
            const togglableToolsFull = fullCategory.tools.filter(
              (tool) => !tool.disableStandaloneToggle
            );
            const allEquipped =
              togglableToolsFull.length > 0 &&
              togglableToolsFull.every((tool) =>
                enabledTools.includes(tool.name)
              );
            const handleCategoryBulkToggle = () => {
              if (allEquipped) {
                const toolNames = togglableToolsFull
                  .filter((tool) => enabledTools.includes(tool.name))
                  .map((tool) => tool.name);
                if (onBulkSetToolsEnabled) {
                  onBulkSetToolsEnabled(toolNames, false);
                  return;
                }
                toolNames.forEach((toolName) => onToggleTool(toolName));
                return;
              }

              const toolNames = togglableTools.map((tool) => tool.name);
              if (onBulkSetToolsEnabled) {
                onBulkSetToolsEnabled(toolNames, true);
                return;
              }
              toolNames.forEach((toolName) => {
                if (!enabledTools.includes(toolName)) onToggleTool(toolName);
              });
            };
            return (
              <Box
                key={category.server?.serverId || category.title}
                sx={{
                  "& .agent-tool-category__bulk-toggle": {
                    opacity: 0,
                    pointerEvents: "none",
                    transition: "opacity 120ms ease",
                  },
                  "&:hover .agent-tool-category__bulk-toggle, &:focus-within .agent-tool-category__bulk-toggle":
                    {
                      opacity: 1,
                      pointerEvents: "auto",
                    },
                  "@media (hover: none)": {
                    "& .agent-tool-category__bulk-toggle": {
                      opacity: 1,
                      pointerEvents: "auto",
                    },
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    mb: 1,
                    pb: 0.5,
                    borderBottom: "2px solid",
                    borderColor: alpha(color, 0.4),
                    width: "100%",
                  }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "2px",
                      backgroundColor: color,
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 700,
                      letterSpacing: 0.4,
                      textTransform: "uppercase",
                      color: "text.primary",
                    }}
                  >
                    {category.title}
                  </Typography>
                  <Box
                    component="span"
                    sx={{
                      fontSize: 11,
                      color: "text.secondary",
                      backgroundColor: "action.hover",
                      px: 0.75,
                      py: "1px",
                      borderRadius: "10px",
                      ml: 0.25,
                    }}
                  >
                    {eq}/{total}
                  </Box>
                  {togglableToolsFull.length > 0 && (
                    <Button
                      className="agent-tool-category__bulk-toggle"
                      size="small"
                      onClick={handleCategoryBulkToggle}
                      sx={{
                        ml: "auto",
                        minWidth: 0,
                        px: 1,
                        py: 0.25,
                        fontSize: 11,
                        fontWeight: 600,
                        lineHeight: 1.4,
                        textTransform: "none",
                        color: "text.secondary",
                        borderColor: "divider",
                      }}
                      variant="outlined"
                    >
                      {allEquipped ? "Unequip all" : "Equip all"}
                    </Button>
                  )}
                </Box>

                {catConfigNode && (
                  <Box
                    sx={{
                      mb: 1.25,
                      p: 1.5,
                      border: 1,
                      borderColor: "divider",
                      borderRadius: "8px",
                      backgroundColor: "action.hover",
                    }}
                  >
                    {!category.hideCategoryConfigLabel && (
                      <Typography
                        sx={{
                          fontSize: 11,
                          fontWeight: 600,
                          letterSpacing: 0.6,
                          textTransform: "uppercase",
                          color: "text.disabled",
                          mb: 1,
                        }}
                      >
                        Shared · Applies to all {category.title} tools
                      </Typography>
                    )}
                    {catConfigNode}
                  </Box>
                )}

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: `repeat(auto-fill, ${TOOL_TILE_SIZE_PX}px)`,
                    gap: "8px",
                    justifyContent: "flex-start",
                  }}
                >
                  {catTools.map(renderTile)}
                  {showCreateCustomToolTile && renderCreateCustomToolTile()}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </>
  );

  return (
    <Box
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: "10px",
        backgroundColor: "background.paper",
        minHeight: { xs: 400, md: 0 },
        minWidth: 0,
        display: "flex",
        alignItems: "flex-start",
        flexDirection: { xs: "column", md: "row" },
      }}
    >
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          p: 2,
          borderRight: detailTool ? { md: 1 } : 0,
          borderBottom: detailTool ? { xs: 1, md: 0 } : 0,
          borderColor: "divider",
        }}
      >
        {renderInventoryView()}
      </Box>
      {detailTool && (
        <Box
          sx={{
            width: { xs: "100%", md: 340 },
            flexShrink: 0,
            alignSelf: { md: "flex-start" },
            position: { md: "sticky" },
            top: { md: 16 },
            maxHeight: { xs: "42vh", md: "calc(100vh - 32px)" },
            overflowY: "auto",
            p: 2,
            backgroundColor: (theme) => alpha(theme.palette.action.hover, 0.35),
          }}
        >
          {renderDetailPanel()}
        </Box>
      )}
    </Box>
  );
}
